import { toast as sonner } from "sonner";

import { isMixpanelInitialized, trackEvent } from "@/lib/analytics";
import { isTestEnv } from "@/lib/env";
import { safeStringify } from "@/lib/safeStringify";
import { TOAST_CONSOLE_PREFIX } from "@/lib/toastBridgeMarker";

type SonnerToast = typeof sonner;
type ConsoleLevel = "error" | "warn" | "info";
type ToastVariant =
  | "error"
  | "warning"
  | "success"
  | "info"
  | "message"
  | "default";
// `loading` is bridged to the console (for the replay/OTel surfaces) but never
// fired as a Mixpanel Activity event — see `wrappedLoading` below.
type LoggedVariant = ToastVariant | "loading";

const describe = (opts: { description?: unknown } | undefined) =>
  typeof opts?.description === "string" ? ` ${opts.description}` : "";

/**
 * The structured property bag shared by BOTH the Mixpanel "Toast Displayed"
 * Activity event and the `[toast]` console payload, so the two never drift.
 *
 * - `message`/`description` are captured only when they're strings; for JSX
 *   (ReactNode) toasts those keys are omitted rather than serialized.
 */
const toastProps = (
  variant: LoggedVariant,
  message: unknown,
  opts: { description?: unknown } | undefined
) => ({
  variant,
  ...(typeof message === "string" ? { message } : {}),
  ...(typeof opts?.description === "string"
    ? { description: opts.description }
    : {}),
});

/**
 * Fire a single "Toast Displayed" Mixpanel event so every toast surfaces in the
 * Activity feed (distinct from the masked replay-console panel the `console.*`
 * bridge already feeds). Mixpanel-only — no Sentry path.
 *
 * No-ops in test env (matches the onboarding/verification convention) and, via
 * `trackEvent`, when Mixpanel isn't initialized (Storybook / localhost).
 */
const trackToast = (
  variant: ToastVariant,
  message: unknown,
  opts: { description?: unknown } | undefined
) => {
  if (isTestEnv) return;
  trackEvent("Toast Displayed", toastProps(variant, message, opts));
};

/**
 * Console bridge: emit a readable `[toast] …` line PLUS a structured JSON
 * payload as a SECOND argument — mirroring `logError()` so the toast shows up
 * richly (variant, message, description) in the Mixpanel session-replay console
 * panel, rather than as a bare string. The same two-arg call also enriches the
 * Sentry breadcrumb and the OTel/Kibana log body. We deliberately do NOT add
 * `pathname`: Mixpanel already records it via `track_pageview: "url-with-path"`,
 * and it's the one field that would ship an unredacted route id (beneficiaryId
 * etc.) to surfaces the rest of the toast payload avoids.
 *
 * Level mapping — TWO modes:
 *
 *  - When Mixpanel is recording (deployed), escalate EVERY variant to
 *    `console.error`. Mixpanel's session-replay event feed surfaces ONLY
 *    `console.error` captures inline; `console.warn`/`console.info` lines never
 *    appear in the timeline, so a warning/success/info toast bridged at its
 *    semantic level would be invisible in the replay feed. The structured JSON
 *    second arg still carries the toast's TRUE `variant`, so downstream Kibana
 *    severity stays accurate — `lib/otel/logger.ts` derives severity from that
 *    variant (via `toastVariantFromConsoleArgs`), NOT from the console method,
 *    so success toasts don't show up as ERROR on the dashboards.
 *  - When Mixpanel is NOT recording (dev / localhost / Storybook / E2E), keep
 *    the semantic mapping (error → console.error, warning → console.warn, rest →
 *    console.info). No replay is recorded there, so escalation would only add
 *    red devtools noise and would break the existing tests' semantics.
 *
 * Sentry impact is breadcrumb-only BY DESIGN — there is no captureConsole
 * integration, so a `[toast]` console call never becomes a Sentry issue. The
 * `[toast]` prefix (shared via `TOAST_CONSOLE_PREFIX`) is the marker
 * `sharedBeforeSend` drops on, which makes that invariant durable if a
 * captureConsole integration is ever added (keep the two in sync). See
 * `.claude/rules/web/analytics.md` → "Toast → Console Bridge".
 */
const logToast = (
  level: ConsoleLevel,
  variant: LoggedVariant,
  message: unknown,
  opts: { description?: unknown } | undefined
) => {
  const fallback =
    variant === "default" ? TOAST_CONSOLE_PREFIX : `[toast.${variant}]`;
  const text = typeof message === "string" ? message : fallback;
  const effectiveLevel: ConsoleLevel = isMixpanelInitialized()
    ? "error"
    : level;
  // eslint-disable-next-line no-console -- this file IS the canonical console bridge
  console[effectiveLevel](
    `${TOAST_CONSOLE_PREFIX} ${text}${describe(opts)}`,
    safeStringify(toastProps(variant, message, opts))
  );
};

const wrappedError: SonnerToast["error"] = (message, opts) => {
  logToast("error", "error", message, opts);
  trackToast("error", message, opts);
  return sonner.error(message, opts);
};

const wrappedWarning: SonnerToast["warning"] = (message, opts) => {
  logToast("warn", "warning", message, opts);
  trackToast("warning", message, opts);
  return sonner.warning(message, opts);
};

const wrappedSuccess: SonnerToast["success"] = (message, opts) => {
  logToast("info", "success", message, opts);
  trackToast("success", message, opts);
  return sonner.success(message, opts);
};

const wrappedInfo: SonnerToast["info"] = (message, opts) => {
  logToast("info", "info", message, opts);
  trackToast("info", message, opts);
  return sonner.info(message, opts);
};

const wrappedMessage: SonnerToast["message"] = (message, opts) => {
  logToast("info", "message", message, opts);
  trackToast("message", message, opts);
  return sonner.message(message, opts);
};

// Loading toasts are deliberately NOT tracked — they're transient (replaced by a
// terminal toast or dismissed), so they'd be noise in the Activity feed. They
// still hit the console bridge so the replay/OTel surfaces see the spinner.
const wrappedLoading: SonnerToast["loading"] = (message, opts) => {
  logToast("info", "loading", message, opts);
  return sonner.loading(message, opts);
};

const callableToast = ((
  message: Parameters<SonnerToast>[0],
  opts?: Parameters<SonnerToast>[1]
) => {
  logToast("info", "default", message, opts);
  trackToast("default", message, opts);
  return sonner(message, opts);
}) as SonnerToast;

export const toast: SonnerToast = Object.assign(callableToast, sonner, {
  error: wrappedError,
  warning: wrappedWarning,
  success: wrappedSuccess,
  info: wrappedInfo,
  message: wrappedMessage,
  loading: wrappedLoading,
});
